/**
 * Header: sticky compression, mobile drawer, submenu toggles.
 */
const MOBILE_BREAKPOINT = 1024;
const SELECTORS = {
    header: '[data-site-header]',
    mobileToggle: '[data-mobile-toggle]',
    mobileClose: '[data-mobile-close]',
    primaryNav: '[data-primary-nav]',
    navItemWithChildren: '.primary-nav__item--has-children',
};
class SiteHeader {
    constructor(header) {
        this.overlay = null;
        this.stickyRaf = 0;
        this.isSticky = false;
        this.isMobileOpen = false;
        this.header = header;
        this.mobileToggle = header.querySelector(SELECTORS.mobileToggle);
        this.mobileCloseButtons = header.querySelectorAll(SELECTORS.mobileClose);
        this.primaryNav = header.querySelector(SELECTORS.primaryNav);
        this.config = window.distinctLaptopsHeader ?? {
            stickyOffset: 60,
            i18n: { menuOpen: 'Open menu', menuClose: 'Close menu' },
        };
        this.init();
    }
    init() {
        this.setHeaderHeightVar();
        this.createOverlay();
        this.bindStickyScroll();
        this.bindMobileToggle();
        this.bindSubmenuToggles();
        this.bindEscapeKey();
        this.bindResize();
        window.addEventListener('load', () => this.setHeaderHeightVar());
    }
    setHeaderHeightVar() {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }
    createOverlay() {
        this.overlay = document.createElement('div');
        this.overlay.className = 'site-header__overlay';
        this.overlay.setAttribute('aria-hidden', 'true');
        this.overlay.addEventListener('click', () => this.closeMobileMenu());
        this.header.after(this.overlay);
    }
    bindStickyScroll() {
        const onScroll = () => {
            if (this.stickyRaf) {
                return;
            }
            this.stickyRaf = window.requestAnimationFrame(() => {
                this.stickyRaf = 0;
                const shouldStick = window.scrollY > this.config.stickyOffset;
                if (shouldStick !== this.isSticky) {
                    this.isSticky = shouldStick;
                    this.header.classList.toggle('is-sticky', shouldStick);
                    document.body.classList.toggle('has-sticky-header', shouldStick);
                    this.setHeaderHeightVar();
                }
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
    }
    bindMobileToggle() {
        this.mobileToggle?.addEventListener('click', () => {
            if (this.isMobileOpen) {
                this.closeMobileMenu();
            }
            else {
                this.openMobileMenu();
            }
        });
        this.mobileCloseButtons.forEach((button) => {
            button.addEventListener('click', () => this.closeMobileMenu());
        });
    }
    openMobileMenu() {
        this.isMobileOpen = true;
        this.header.classList.add('is-mobile-open');
        document.body.classList.add('mobile-nav-open');
        this.overlay?.classList.add('is-visible');
        this.mobileToggle?.setAttribute('aria-expanded', 'true');
    }
    closeMobileMenu() {
        this.isMobileOpen = false;
        this.header.classList.remove('is-mobile-open');
        document.body.classList.remove('mobile-nav-open');
        this.overlay?.classList.remove('is-visible');
        this.mobileToggle?.setAttribute('aria-expanded', 'false');
    }
    bindSubmenuToggles() {
        if (!this.primaryNav) {
            return;
        }
        this.primaryNav.querySelectorAll(SELECTORS.navItemWithChildren).forEach((item) => {
            const link = item.querySelector(':scope > a');
            if (!link) {
                return;
            }
            link.addEventListener('click', (event) => {
                if (window.innerWidth >= MOBILE_BREAKPOINT) {
                    return;
                }
                event.preventDefault();
                item.classList.toggle('primary-nav__item--expanded');
                link.setAttribute('aria-expanded', item.classList.contains('primary-nav__item--expanded') ? 'true' : 'false');
            });
        });
    }
    bindEscapeKey() {
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isMobileOpen) {
                this.closeMobileMenu();
            }
        });
    }
    bindResize() {
        let timer = 0;
        window.addEventListener('resize', () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                this.setHeaderHeightVar();
                if (window.innerWidth >= MOBILE_BREAKPOINT && this.isMobileOpen) {
                    this.closeMobileMenu();
                }
            }, 150);
        });
    }
}
export function initHeader() {
    const header = document.querySelector(SELECTORS.header);
    if (header) {
        new SiteHeader(header);
    }
}
